import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Botao,
  Campo,
  CampoDeSelecao,
  CampoDeTexto,
  EstadoCarregando,
  Modal,
  Selo,
  juntarClasses,
  useDialogo,
  type TomSemantico,
} from '../../componentes/ui';
import { useIdentidade } from '../../hooks/useSessao';
import {
  CORES_DE_TAG,
  carregarCard,
  carregarParticipantesPossiveis,
  comentar,
  criarTag,
  definirData,
  definirParticipantes,
  definirTags,
  editarComentario,
  excluirCard,
  excluirComentario,
  formatarDataIso,
  moverCard,
  situacaoDoPrazo,
  type AutomacaoAplicada,
  type CardDetalhado,
  type ColunaDoQuadro,
  type TagDoCrm,
} from '../../servicos/crm';
import { formatarCnpj } from '../../servicos/formato';
import { PastaDoDrive } from '../DriveDaEmpresa';
import { ErroDaApi } from '../ErroDaApi';
import { CopiarParaQuadro } from './CopiarParaQuadro';
import { formatarTelefone } from '../vinculos-trabalhistas';
import { AtividadesDoCard } from './AtividadesDoCard';
import { CaptadoresDoCard } from './CaptadoresDoCard';
import { ContatosDoCard } from './ContatosDoCard';
import { iniciais } from './CardDoPipeline';

/**
 * O card aberto, no molde do Trello: à esquerda o que se lê (a empresa, quem
 * trabalha nela, os comentários); à direita o que se ajusta (coluna, data,
 * tags, quem acompanha).
 *
 * A descrição é o cadastro da empresa, lido na hora — não um texto do card.
 * Informação de acompanhamento vai nos comentários, que guardam quem escreveu
 * e quando.
 *
 * Toda alteração grava na hora e recarrega o quadro por trás: não há "Salvar"
 * que se possa esquecer de apertar ao fechar a janela.
 */
export function DetalheDoCard(props: {
  readonly cardId: string;
  readonly colunas: readonly ColunaDoQuadro[];
  readonly tags: readonly TagDoCrm[];
  readonly aoFechar: () => void;
  /** Trocar a coluna levou a empresa a outro quadro (automação do CRM). */
  readonly aoAutomatizar?: (aplicada: AutomacaoAplicada, empresa: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const card = useQuery({
    queryKey: ['crm', 'card', props.cardId],
    queryFn: () => carregarCard(props.cardId),
  });

  const recarregar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['crm'] });
  };

  const c = card.data;

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo={c?.razaoSocial ?? 'Card'}
      {...(c === undefined
        ? {}
        : { descricao: `CNPJ ${formatarCnpj(c.cnpj)} · na coluna ${c.colunaNome}` })}
      largura="ampla"
      rodape={
        <Botao variante="secundario" onClick={props.aoFechar}>
          Fechar
        </Botao>
      }
    >
      {card.isError ? (
        <ErroDaApi titulo="Não foi possível abrir o card" erro={card.error} />
      ) : c === undefined ? (
        <EstadoCarregando contexto="o card" />
      ) : (
        <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="flex min-w-0 flex-col gap-8">
            {/* O que falta fazer vem antes de quem a empresa é: é o motivo de abrir o card. */}
            <AtividadesDoCard cardId={c.id} />
            <CaptadoresDoCard card={c} tags={props.tags} />
            <Descricao card={c} />
            <Colaboradores card={c} />
            <Comentarios card={c} aoMudar={recarregar} />
          </div>
          <Ajustes
            card={c}
            colunas={props.colunas}
            tags={props.tags}
            aoMudar={recarregar}
            aoExcluir={props.aoFechar}
            {...(props.aoAutomatizar === undefined ? {} : { aoAutomatizar: props.aoAutomatizar })}
          />
        </div>
      )}
    </Modal>
  );
}

// ─── Coluna principal ───────────────────────────────────────────────────────

function Secao(props: {
  readonly titulo: string;
  readonly acao?: ReactNode;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-conteudo-sutil">
          {props.titulo}
        </h3>
        {props.acao}
      </div>
      {props.children}
    </section>
  );
}

function Dado(props: {
  readonly rotulo: string;
  readonly children: ReactNode;
  readonly largo?: boolean;
}): JSX.Element {
  return (
    <div
      className={juntarClasses('flex flex-col gap-0.5', props.largo === true && 'sm:col-span-2')}
    >
      <dt className="text-xs text-conteudo-sutil">{props.rotulo}</dt>
      <dd className="text-sm text-conteudo-primario">{props.children}</dd>
    </div>
  );
}

const vazio = (valor: string | null): string =>
  valor === null || valor.trim() === '' ? '—' : valor;

function Descricao(props: { readonly card: CardDetalhado }): JSX.Element {
  const { card } = props;
  const e = card.empresa;
  const perfis = [
    e.perfis.cliente && 'Cliente',
    e.perfis.assessor && 'Assessor',
    e.perfis.fornecedor && 'Fornecedor',
  ].filter((p): p is string => p !== false);

  return (
    <Secao
      titulo="Descrição"
      acao={
        <Link
          to={`/empresas/${card.businessId}`}
          className="text-xs text-conteudo-secundario underline-offset-4 hover:text-conteudo-primario hover:underline"
        >
          Abrir cadastro →
        </Link>
      }
    >
      <dl className="grid gap-4 sm:grid-cols-2">
        <Dado rotulo="Nome fantasia">{vazio(card.nomeFantasia)}</Dado>
        <Dado rotulo="CNPJ">
          <span className="valor-fiscal">{formatarCnpj(card.cnpj)}</span>
        </Dado>
        <Dado rotulo="E-mail">{e.emailAddress}</Dado>
        <Dado rotulo="Telefone">{e.telefone === null ? '—' : formatarTelefone(e.telefone)}</Dado>
        <Dado rotulo="Endereço" largo>
          {vazio(e.endereco)}
        </Dado>
        <Dado rotulo="Regime tributário">{vazio(e.regimeTributario)}</Dado>
        <Dado rotulo="Natureza jurídica">{vazio(e.naturezaJuridica)}</Dado>
        <Dado rotulo="CNAE principal">{vazio(e.cnae)}</Dado>
        <Dado rotulo="CAD/ICMS">{vazio(e.cadIcms)}</Dado>
        <Dado rotulo="Credencial SISCRED">{vazio(e.credencialSiscred)}</Dado>
        <Dado rotulo="Perfil na operação">
          {perfis.length === 0 ? (
            'Sem perfil'
          ) : (
            <span className="flex flex-wrap gap-1">
              {perfis.map((p) => (
                <Selo key={p} tom="informativo" tamanho="pequeno">
                  {p}
                </Selo>
              ))}
            </span>
          )}
        </Dado>
        <Dado rotulo="Cadastrada em">{new Date(e.cadastradaEm).toLocaleDateString('pt-BR')}</Dado>
        <Dado rotulo="No pipeline desde">
          {new Date(card.criadoEm).toLocaleDateString('pt-BR')}
        </Dado>
      </dl>
      {/* A pasta no Drive é dado cadastral, como no cadastro da empresa. */}
      <PastaDoDrive businessId={card.businessId} />
    </Secao>
  );
}

function Colaboradores(props: { readonly card: CardDetalhado }): JSX.Element {
  const lista = props.card.colaboradores;
  return (
    <Secao titulo={`Colaboradores da empresa (${lista.length})`}>
      {lista.length === 0 ? (
        <p className="text-sm text-conteudo-secundario">
          Nenhum colaborador cadastrado nesta empresa. Cadastre pela aba Colaboradores do cadastro.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-borda/60 rounded-card border border-borda">
          {lista.map((p) => (
            <li
              key={p.emailAddress}
              className="flex flex-wrap items-start justify-between gap-2 px-3 py-2.5"
            >
              <span className="flex min-w-0 flex-col">
                <span className="text-sm font-medium text-conteudo-primario">{p.nome}</span>
                <span className="text-xs text-conteudo-secundario">
                  {p.cargo ?? 'Cargo não informado'}
                </span>
                <span className="text-xs text-conteudo-sutil">
                  {p.emailAddress}
                  {p.telefone !== null && ` · ${formatarTelefone(p.telefone)}`}
                </span>
              </span>
              <span className="flex flex-wrap gap-1">
                <Selo tom={p.papel === 'Gestor' ? 'progresso' : 'neutro'} tamanho="pequeno">
                  {p.papel}
                </Selo>
                {p.assinaPelaEmpresa && (
                  <Selo tom="informativo" tamanho="pequeno">
                    Assina
                  </Selo>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <ContatosDoCard card={props.card} />
    </Secao>
  );
}

function Comentarios(props: {
  readonly card: CardDetalhado;
  readonly aoMudar: () => void;
}): JSX.Element {
  const identidade = useIdentidade();
  const dialogo = useDialogo();
  const [texto, setTexto] = useState('');

  const novo = useMutation({
    mutationFn: () => comentar(props.card.id, texto.trim()),
    onSuccess: () => setTexto(''),
    onSettled: props.aoMudar,
  });
  const editar = useMutation({
    mutationFn: (m: { id: string; texto: string }) => editarComentario(m.id, m.texto),
    onSettled: props.aoMudar,
  });
  const apagar = useMutation({ mutationFn: excluirComentario, onSettled: props.aoMudar });

  const lista = props.card.comentariosDoCard;
  const erro = novo.error ?? editar.error ?? apagar.error;

  return (
    <Secao titulo={`Comentários (${lista.length})`}>
      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (texto.trim() !== '') novo.mutate();
        }}
      >
        <CampoDeTexto
          rotulo="Novo comentário"
          rotuloOculto
          rows={3}
          placeholder="Andamento, próximo passo, combinado com o cliente…"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <div className="flex justify-end">
          <Botao
            type="submit"
            tamanho="pequeno"
            disabled={texto.trim() === ''}
            carregando={novo.isPending}
          >
            Comentar
          </Botao>
        </div>
      </form>

      {erro !== null && <ErroDaApi titulo="Não foi possível salvar o comentário" erro={erro} />}

      {lista.length > 0 && (
        <ol className="flex flex-col gap-3">
          {lista.map((com) => {
            const meu = com.autor.accountId === identidade?.accountId;
            return (
              <li key={com.id} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-acento-indigo/15 text-xs font-semibold text-acento-indigo"
                >
                  {iniciais(com.autor.nome)}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <p className="text-xs text-conteudo-sutil">
                      <span className="font-medium text-conteudo-primario">{com.autor.nome}</span>
                      {' · '}
                      {new Date(com.criadoEm).toLocaleString('pt-BR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                      {com.editado && ' · editado'}
                    </p>
                    {meu && (
                      <span className="flex gap-3 text-xs">
                        <button
                          type="button"
                          className="text-conteudo-secundario underline-offset-2 hover:underline"
                          onClick={async () => {
                            const novoTexto = await dialogo.pedirTexto({
                              titulo: 'Editar comentário',
                              rotulo: 'Comentário',
                              valorInicial: com.texto,
                              multilinha: true,
                              obrigatorio: true,
                              rotuloConfirmar: 'Salvar',
                            });
                            if (novoTexto !== null && novoTexto !== com.texto) {
                              editar.mutate({ id: com.id, texto: novoTexto });
                            }
                          }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="text-erro underline-offset-2 hover:underline"
                          onClick={async () => {
                            const ok = await dialogo.confirmar({
                              titulo: 'Apagar este comentário?',
                              rotuloConfirmar: 'Apagar',
                              tom: 'perigo',
                            });
                            if (ok) apagar.mutate(com.id);
                          }}
                        >
                          Apagar
                        </button>
                      </span>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap break-words rounded-card bg-superficie-base px-3 py-2 text-sm text-conteudo-primario">
                    {com.texto}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Secao>
  );
}

// ─── Coluna de ajustes ──────────────────────────────────────────────────────

/** Valor do seletor de tags que abre o formulário de tag nova. */
const CRIAR_TAG = '__criar__';

function Ajustes(props: {
  readonly card: CardDetalhado;
  readonly colunas: readonly ColunaDoQuadro[];
  readonly tags: readonly TagDoCrm[];
  readonly aoMudar: () => void;
  readonly aoExcluir: () => void;
  readonly aoAutomatizar?: (aplicada: AutomacaoAplicada, empresa: string) => void;
}): JSX.Element {
  const { card } = props;
  const dialogo = useDialogo();
  const [copiando, setCopiando] = useState(false);
  const [criandoTag, setCriandoTag] = useState(false);
  const [nomeDaTag, setNomeDaTag] = useState('');
  const [corDaTag, setCorDaTag] = useState<TomSemantico>('informativo');

  const possiveis = useQuery({
    queryKey: ['crm', 'participantes-possiveis'],
    queryFn: carregarParticipantesPossiveis,
  });

  const opcoes = { onSettled: props.aoMudar };
  // Índice além do fim: o servidor põe no último lugar da coluna.
  const mover = useMutation({
    mutationFn: (colunaId: string) => moverCard(card.id, colunaId, Number.MAX_SAFE_INTEGER),
    onSuccess: (r) => {
      if (r.automacao !== null) props.aoAutomatizar?.(r.automacao, card.razaoSocial);
    },
    ...opcoes,
  });
  const data = useMutation({
    mutationFn: (iso: string | null) => definirData(card.id, iso),
    ...opcoes,
  });
  const tags = useMutation({
    mutationFn: (ids: readonly string[]) => definirTags(card.id, ids),
    ...opcoes,
  });
  const novaTag = useMutation({
    mutationFn: async () => {
      const tag = await criarTag(nomeDaTag.trim(), corDaTag);
      await definirTags(card.id, [...card.tagIds, tag.id]);
    },
    onSuccess: () => {
      setNomeDaTag('');
      setCriandoTag(false);
    },
    ...opcoes,
  });
  const participantes = useMutation({
    mutationFn: (ids: readonly string[]) => definirParticipantes(card.id, ids),
    ...opcoes,
  });
  const apagar = useMutation({
    mutationFn: () => excluirCard(card.id),
    onSuccess: props.aoExcluir,
    ...opcoes,
  });

  const erro =
    mover.error ?? data.error ?? tags.error ?? novaTag.error ?? participantes.error ?? apagar.error;
  const acompanham = new Set(card.participantes.map((p) => p.accountId));
  const marcadas = props.tags.filter((t) => card.tagIds.includes(t.id));
  const naoMarcadas = props.tags.filter((t) => !card.tagIds.includes(t.id));
  const podemEntrar = (possiveis.data ?? []).filter((p) => !acompanham.has(p.accountId));
  const prazo = card.dataExecucao === null ? null : situacaoDoPrazo(card.dataExecucao);

  return (
    <aside className="flex flex-col gap-6">
      {erro !== null && <ErroDaApi titulo="Não foi possível salvar" erro={erro} />}

      <div className="flex flex-col gap-2">
        <CampoDeSelecao
          rotulo="Coluna"
          value={card.colunaId}
          onChange={(e) => mover.mutate(e.target.value)}
          opcoes={props.colunas.map((col) => ({ valor: col.id, rotulo: col.nome }))}
        />
        {/* A mesma empresa em outro quadro (o Operacional, por exemplo). */}
        <button
          type="button"
          className="self-start text-sm text-acento-ciano underline-offset-4 hover:underline"
          onClick={() => setCopiando(true)}
        >
          Copiar para um quadro
        </button>
      </div>
      <CopiarParaQuadro aberto={copiando} card={card} aoFechar={() => setCopiando(false)} />

      <div className="flex flex-col gap-1.5">
        <Campo
          rotulo="Data de execução"
          type="date"
          value={card.dataExecucao ?? ''}
          onChange={(e) => data.mutate(e.target.value === '' ? null : e.target.value)}
          ajuda={
            prazo === 'vencida'
              ? `Vencida em ${formatarDataIso(card.dataExecucao ?? '')}.`
              : prazo === 'hoje'
                ? 'É hoje.'
                : 'É por ela que o card aparece no calendário.'
          }
          {...(prazo === 'vencida' ? { erro: 'Data vencida.' } : {})}
        />
        {card.dataExecucao !== null && (
          <button
            type="button"
            className="self-start text-xs text-conteudo-secundario underline-offset-2 hover:underline"
            onClick={() => data.mutate(null)}
          >
            Tirar a data
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-conteudo-secundario">Tags</p>
        {/*
          Só as tags do card ficam à vista; as outras moram no seletor. A lista
          inteira, com as não marcadas apagadas, fazia o card parecer ter todas.
        */}
        {marcadas.length === 0 ? (
          <p className="text-xs text-conteudo-sutil">Nenhuma tag neste card.</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5" aria-label="Tags do card">
            {marcadas.map((tag) => (
              <li key={tag.id}>
                <Selo tom={tag.cor} tamanho="pequeno" semPonto>
                  <span className="flex items-center gap-1.5">
                    {tag.nome}
                    <button
                      type="button"
                      aria-label={`Tirar a tag ${tag.nome}`}
                      className="opacity-60 hover:opacity-100"
                      onClick={() => tags.mutate(card.tagIds.filter((id) => id !== tag.id))}
                    >
                      ✕
                    </button>
                  </span>
                </Selo>
              </li>
            ))}
          </ul>
        )}
        {!criandoTag && (
          <CampoDeSelecao
            rotulo="Adicionar tag"
            rotuloOculto
            value=""
            onChange={(e) => {
              const valor = e.target.value;
              if (valor === CRIAR_TAG) setCriandoTag(true);
              else if (valor !== '') tags.mutate([...card.tagIds, valor]);
            }}
            opcoes={[
              { valor: '', rotulo: '+ Adicionar tag…' },
              ...naoMarcadas.map((t) => ({ valor: t.id, rotulo: t.nome })),
              { valor: CRIAR_TAG, rotulo: 'Criar nova tag…' },
            ]}
          />
        )}
        {criandoTag && (
          <form
            className="flex flex-col gap-2 rounded-card border border-borda p-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (nomeDaTag.trim() !== '') novaTag.mutate();
            }}
          >
            <Campo
              rotulo="Nome da tag"
              value={nomeDaTag}
              maxLength={60}
              autoFocus
              onChange={(e) => setNomeDaTag(e.target.value)}
            />
            <CampoDeSelecao
              rotulo="Cor"
              value={corDaTag}
              onChange={(e) => setCorDaTag(e.target.value as TomSemantico)}
              opcoes={CORES_DE_TAG.map((c) => ({ valor: c.tom, rotulo: c.rotulo }))}
            />
            <div className="flex gap-2">
              <Botao
                type="submit"
                tamanho="pequeno"
                disabled={nomeDaTag.trim() === ''}
                carregando={novaTag.isPending}
              >
                Criar e aplicar
              </Botao>
              <Botao variante="sutil" tamanho="pequeno" onClick={() => setCriandoTag(false)}>
                Cancelar
              </Botao>
            </div>
          </form>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-conteudo-secundario">Acompanhando</p>
        {card.participantes.length === 0 ? (
          <p className="text-xs text-conteudo-sutil">Ninguém acompanha este card ainda.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {card.participantes.map((p) => (
              <li key={p.accountId} className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2 text-sm">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-acento-indigo/15 text-[0.6rem] font-semibold text-acento-indigo">
                    {iniciais(p.nome)}
                  </span>
                  <span className="truncate">{p.nome}</span>
                </span>
                <button
                  type="button"
                  aria-label={`Tirar ${p.nome} do card`}
                  className="text-xs text-conteudo-sutil hover:text-erro"
                  onClick={() =>
                    participantes.mutate(
                      card.participantes.map((x) => x.accountId).filter((id) => id !== p.accountId),
                    )
                  }
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
        {podemEntrar.length > 0 && (
          <CampoDeSelecao
            rotulo="Adicionar quem acompanha"
            rotuloOculto
            value=""
            onChange={(e) => {
              if (e.target.value !== '') {
                participantes.mutate([...acompanham, e.target.value]);
              }
            }}
            opcoes={[
              { valor: '', rotulo: '+ Adicionar pessoa…' },
              ...podemEntrar.map((p) => ({ valor: p.accountId, rotulo: p.nome })),
            ]}
          />
        )}
      </div>

      <div className="border-t border-borda pt-4">
        <Botao
          variante="perigo"
          tamanho="pequeno"
          carregando={apagar.isPending}
          onClick={async () => {
            const ok = await dialogo.confirmar({
              titulo: `Tirar ${card.razaoSocial} deste quadro?`,
              descricao:
                'O card é excluído deste quadro com os comentários dele. A empresa continua cadastrada — e nos outros quadros em que estiver — e pode voltar depois.',
              rotuloConfirmar: 'Excluir card',
              tom: 'perigo',
            });
            if (ok) apagar.mutate();
          }}
        >
          Excluir card
        </Botao>
      </div>
    </aside>
  );
}
